/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Four Elements AI Split-Screen Authentication Layout (Task 3.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import {
  Sparkles,
  X,
  Users,
  Globe,
  Zap,
} from 'lucide-react';
import { useI18nStore } from '../../stores/useI18nStore';
import { resolvePlatformDomains } from '@sofia/shared-types';

export interface FourElementsAuthLayoutProps {
  mode: 'login' | 'register';
  siteName: string;
  slogan?: string;
  onClose?: () => void;
  children: React.ReactNode;
}

export const FourElementsAuthLayout: React.FC<FourElementsAuthLayoutProps> = ({
  mode,
  siteName,
  slogan,
  onClose,
  children,
}) => {
  const { t } = useI18nStore();

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      const domains = resolvePlatformDomains();
      window.location.href = domains.siteOrigin;
    }
  };

  return (
    <div className="min-h-screen w-full bg-background-base text-foreground flex flex-col justify-between selection:bg-primary/20 selection:text-primary">
      <main className="flex-1 flex flex-col justify-center">
        <section className="py-4 sm:py-6 lg:py-8 px-4 sm:px-6 lg:px-8 max-w-[1600px] w-full mx-auto my-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch min-h-[calc(100vh-4rem)]">
            {/* 1. Left Column: Split-Screen Hero Showcase (lg+) */}
            <div className="hidden lg:flex lg:col-span-6 xl:col-span-6 relative flex-col justify-between p-8 xl:p-12 rounded-3xl xl:rounded-[2.5rem] border border-surface-border bg-gradient-to-br from-surface-elevated via-surface to-primary/5 shadow-glass overflow-hidden">
              {/* Ambient Glow Orbs */}
              <div className="absolute -top-28 -left-28 w-80 h-80 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-28 -right-28 w-80 h-80 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

              {/* Showcase Top: Platform Badge */}
              <div className="relative z-10 flex items-center justify-between">
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-primary/20 bg-primary/10 text-primary text-xs font-semibold backdrop-blur-sm shadow-sm">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  <span>{t('auth.heroBadge')}</span>
                </div>
              </div>

              {/* Showcase Center: Headline, Slogan & Realistic Studio Mockup */}
              <div className="relative z-10 my-auto py-8 space-y-6">
                <div className="space-y-3">
                  <h2 className="text-3xl xl:text-4xl font-heading font-extrabold text-foreground tracking-tight leading-snug">
                    {mode === 'login' ? (
                      <>
                        Unlock the Power of <br />
                        <span className="text-primary font-black">{siteName}</span> Studio
                      </>
                    ) : (
                      <>
                        Create, Innovate & Scale with <br />
                        <span className="text-primary font-black">{siteName}</span> Edge AI
                      </>
                    )}
                  </h2>
                  <p className="text-foreground-muted text-xs sm:text-sm max-w-lg leading-relaxed">
                    {slogan ||
                      (mode === 'login'
                        ? t('auth.heroSubtitle')
                        : 'Join creators building next-generation articles, multimodal media, and neural voiceovers.')}
                  </p>
                </div>

                {/* Studio Screen Mockup Container */}
                <div className="relative group pt-2">
                  <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-primary/20 to-accent/20 blur-xl opacity-50 group-hover:opacity-80 transition duration-500" />
                  <div className="relative rounded-2xl overflow-hidden border border-surface-border bg-surface/90 shadow-2xl backdrop-blur-sm">
                    <img
                      src="/templates/fourelements-ai/images/screens/screen-5.png"
                      alt={`${siteName} Creative Studio Mockup`}
                      className="w-full h-auto object-cover transform group-hover:scale-[1.01] transition-transform duration-500"
                      onError={(e) => {
                        (e.currentTarget as HTMLElement).style.display = 'none';
                        const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div
                      style={{ display: 'none' }}
                      className="w-full aspect-[16/10] bg-surface-elevated flex flex-col items-center justify-center p-8 text-center space-y-3"
                    >
                      <div className="size-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-glow-primary">
                        <Sparkles className="size-7 text-primary" />
                      </div>
                      <span className="font-heading font-bold text-foreground text-base">
                        {siteName} Creative Studio
                      </span>
                      <span className="text-xs text-foreground-muted max-w-xs">
                        Edge-Native Text, Image, Audio & Video Generation Suite
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Showcase Bottom: 3 Achievement & Cloudflare Security Badges */}
              <div className="relative z-10 grid grid-cols-3 gap-3 pt-6 border-t border-surface-border/60">
                <div className="flex items-center gap-2.5 p-3 rounded-2xl bg-surface/80 border border-surface-border/60 text-xs shadow-sm backdrop-blur-sm">
                  <div className="size-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Users className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="font-bold text-foreground truncate">100k+</span>
                    <span className="text-[10px] text-foreground-muted truncate font-medium">
                      {t('auth.badgeCreators')}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 p-3 rounded-2xl bg-surface/80 border border-surface-border/60 text-xs shadow-sm backdrop-blur-sm">
                  <div className="size-8 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
                    <Globe className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="font-bold text-foreground truncate">330+</span>
                    <span className="text-[10px] text-foreground-muted truncate font-medium">
                      {t('auth.badgeEdge')}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 p-3 rounded-2xl bg-surface/80 border border-surface-border/60 text-xs shadow-sm backdrop-blur-sm">
                  <div className="size-8 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center shrink-0">
                    <Zap className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="font-bold text-foreground truncate">&lt; 15ms</span>
                    <span className="text-[10px] text-foreground-muted truncate font-medium">
                      {t('auth.badgeLatency')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Right Column: Authentication Card & Close Trigger */}
            <div className="col-span-1 lg:col-span-6 xl:col-span-6 flex flex-col justify-between h-full">
              {/* Top Navigation Strip with Close / Exit Button */}
              <div className="flex items-center justify-end w-full pb-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="size-10 rounded-xl border border-surface-border bg-surface text-foreground-muted hover:text-foreground hover:bg-muted/60 transition-colors flex items-center justify-center shadow-sm"
                  title={t('auth.returnHome')}
                  aria-label={t('auth.returnHome')}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Centered Auth Card Viewport */}
              <div className="my-auto w-full flex justify-center py-4">
                {children}
              </div>

              {/* Bottom Spacer for Visual Symmetry */}
              <div className="hidden lg:block h-6" />
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};
